// Kinde authentication client (C-2 dashboard login cutover).
//
// Uses the VANILLA @kinde-oss/kinde-auth-pkce-js singleton — NOT the React SDK —
// because the token seam (`superFetch.getToken`) is a plain module, not a hook.
// The vanilla client's `getToken()` silently refreshes and is callable from any
// module, which is exactly the seam we need (and avoids the hook-only 401-storm).
//
// The whole Kinde path is dark behind the build-time `REACT_APP_KINDE_AUTH` flag.
// When the flag is off, `getKindeClient()` resolves to null and every caller falls
// back to the unchanged Firebase path (one-line rollback = flag off).

import createKindeClient from '@kinde-oss/kinde-auth-pkce-js';
import { jwtDecode } from 'jwt-decode';
import { env } from 'config/env';

// Build-time flag, so this is a constant per build. Read via config/env — the one module that
// touches the bundler-substituted values.
export const isKindeEnabled = () => env.kinde.enabled;

/**
 * Is Kinde the FRONT DOOR, or merely available?
 *
 * These are two different questions and they used to share one flag, which is a trap:
 * `REACT_APP_KINDE_AUTH` was set on prd to ARM Kinde for testing (access app, staff), on the
 * understanding that Firebase stayed the default and Kinde was opt-in behind the logo. Promoting a
 * change that makes Kinde primary would then have silently flipped the front door for every
 * production user — no further flag flip, no announcement.
 *
 * So they are split:
 *   REACT_APP_KINDE_AUTH=true                          Kinde is AVAILABLE (opt-in behind the logo)
 *   REACT_APP_KINDE_AUTH=true + KINDE_PRIMARY=true     Kinde is the FRONT DOOR, Firebase behind the logo
 *   (neither)                                          pure Firebase, page unchanged
 *
 * PRIMARY implies ENABLED — setting primary alone does nothing, so a half-configured environment
 * fails to the safe state rather than to a broken one.
 *
 * ⚠️ NOT per-org, and it cannot be. The sign-in page runs BEFORE authentication, so we do not know
 * who the user is, therefore not their org. The backend `kinde-auth` flag IS per-org, but it governs
 * whether a Kinde token is ACCEPTED — after login, not which door is shown before it. True per-org
 * routing needs identifier-first discovery (type email → unauthenticated endpoint resolves the org's
 * IdP → route), which is a backend endpoint plus an email-enumeration decision.
 */
export const isKindePrimary = () => env.kinde.primary;

/**
 * The BACKEND environment this session is pointed at — the same `selectedEnvironment` key that
 * already steers the API base (superFetch) and the Firebase project (firebase.config).
 *
 * Read from localStorage rather than the Redux store on purpose: this module is imported by the
 * token seam, and reaching for the store here would make auth depend on store init order.
 * firebase.config.js reads the identical key the same way.
 *
 * Read at CALL time, not module load, so a switch mid-session is observed. Any throw (private
 * mode, blocked site data) degrades to this build's own env — never to a foreign tenant.
 */
const selectedEnvironment = () => {
	try {
		return localStorage.getItem('selectedEnvironment') || env.appEnv;
	} catch {
		return env.appEnv;
	}
};

/**
 * The Kinde client id this session's tokens must name in `azp`.
 *
 * ⚠️ Follows the environment switch. On staging set to Production this is the PROD client id, not
 * the build's default — if it did NOT follow, `tokenAzpMatches` would compare a prod-minted token
 * against the dev client id, read a definite mismatch, and log the user out on every request.
 * Outside staging `forEnvironment` returns the single configured tenant, so this is exactly
 * `env.kinde.clientId`, unchanged.
 */
export const getKindeClientId = () => env.kinde.forEnvironment(selectedEnvironment()).clientId;

/**
 * Whether a token was minted for THIS app's Kinde client, read from the `azp`
 * (authorized party) claim.
 *
 * Why this exists: the dashboard (`b24fe14e…`) and the Partner app (`39e0e7f4…`) are separate
 * Kinde clients that share the `auth.zezamii.com` SSO cookie (the gate-E first-party design).
 * A refresh-token exchange run by one app can therefore return tokens minted for the OTHER
 * client. The SDK does notice — `isTokenValid` throws `azp claim mismatch` — but the legacy
 * `getToken()` path CATCHES that throw and only `console.error`s it, so the failure is invisible
 * to us: `getToken()` resolves undefined, our fallback serves whatever stale token is still in
 * the store, the API 401s, the 401 recovery forces another refresh, and that loop is what hangs
 * login and logout. Detecting it ourselves is the only way to break the cycle.
 *
 * ⚠️ FAILS OPEN, deliberately. A false positive logs a working user out, which is far worse than
 * missing a mismatch — so anything short of a DEFINITE mismatch (both values present and
 * different) is treated as ours: no client id configured, an undecodable token, or no `azp`
 * claim. Kinde does always emit `azp` on both the ID and access token (the SDK's own
 * `PAYLOAD_CLAIMS` requires it), so the absent-claim case should never fire in practice — it is
 * there so a provider change degrades to today's behaviour instead of a mass logout.
 *
 * @param {string} token    raw JWT
 * @param {string} clientId this build's Kinde client id
 * @returns {boolean} false ONLY on a definite foreign-client token
 */
export const tokenAzpMatches = (token, clientId) => {
	if (!token || !clientId) return true;
	let azp;
	try {
		({ azp } = jwtDecode(token));
	} catch {
		return true; // undecodable — not this check's call to make
	}
	if (!azp) return true;
	return azp === clientId;
};

// Registrable domain (eTLD+1) for the hosts we actually serve on — all of which sit on a
// two-label public suffix (`.com`, `.net`). This is NOT a general public-suffix parser; it
// would be wrong for a `.com.au`/`.co.uk` host, which is why `firstPartyCookiesUsable` also
// requires a non-`*.kinde.com` custom domain. Kept deliberately small — the only origins in
// play are `cloud.zezamii.com` (prod), the dev SWA `*.azurestaticapps.net`, and the Kinde
// tenants (`auth.zezamii.com` custom / `*.kinde.com` raw).
const registrableDomain = (host) =>
	String(host || '')
		.split('.')
		.slice(-2)
		.join('.');

/**
 * Whether the Kinde session can persist in a FIRST-PARTY cookie instead of localStorage.
 *
 * Kinde's silent-renew stores the session in a cookie on the AUTH domain and reads it from a
 * hidden iframe. That cookie is first-party — and so not blocked — only when the auth domain is
 * SAME-SITE with the dashboard origin (shares the registrable domain). That is true ONLY for the
 * prod custom domain: `auth.zezamii.com` ↔ `cloud.zezamii.com`, both under `zezamii.com`. The dev
 * tenant is a raw `*.kinde.com` domain, cross-site with the SWA origin, so its silent-renew cookie
 * is a blocked third-party cookie and localStorage is the only thing that survives a refresh.
 *
 * Fails CLOSED to localStorage on any uncertainty (unparseable host, `*.kinde.com` auth domain,
 * or a registrable-domain mismatch) — localStorage always works; the only cost of a false negative
 * is the weaker storage we are trying to move away from, never a broken login.
 *
 * Exported for unit testing; not part of the client's public surface.
 * @param {string} kindeDomain REACT_APP_KINDE_DOMAIN (e.g. https://auth.zezamii.com)
 * @param {string} appHost     window.location.hostname (e.g. cloud.zezamii.com)
 * @returns {boolean}
 */
export const firstPartyCookiesUsable = (kindeDomain, appHost) => {
	let kindeHost;
	try {
		kindeHost = new URL(kindeDomain).hostname;
	} catch {
		return false; // no/garbage domain → localStorage
	}
	// A raw Kinde-hosted tenant is never same-site with our apps.
	if (!kindeHost || kindeHost.endsWith('.kinde.com')) return false;
	if (!appHost) return false;
	return registrableDomain(kindeHost) === registrableDomain(appHost);
};

// Memoised singleton promise. The FIRST call to createKindeClient() also processes
// any `?code=…` redirect present in the URL (the /callback exchange), so all callers
// awaiting this shared promise observe a fully-initialised, redirect-processed client.
let clientPromise = null;
// Which backend environment the memoised client was BUILT for. On staging the tenant follows the
// environment switch, so the memo is only valid while that selection is unchanged — see the
// staleness check in getKindeClient. Outside staging this never varies.
let clientEnvironment = null;

/**
 * Drop the memoised client so the next `getKindeClient()` builds a fresh one.
 *
 * Note this does NOT clear the SDK's token store: that store is a module-level singleton inside
 * the SDK (in-memory unless `is_dangerously_use_local_storage`), so a rebuilt client reads the
 * same tokens. Discarding a foreign session therefore relies on the full-page login redirect to
 * drop the in-memory store, plus an explicit removal of the one durable artefact — the legacy
 * `kinde_refresh_token` localStorage key. See `discardForeignKindeSession` in authProvider.
 */
export const resetKindeClient = () => {
	clientPromise = null;
	clientEnvironment = null;
};

/**
 * Resolve the Kinde client singleton, or null when the flag is off.
 * Never throws — on init failure it resolves null so callers fall back to Firebase.
 * @returns {Promise<import('@kinde-oss/kinde-auth-pkce-js').KindeClient|null>}
 */
export const getKindeClient = () => {
	if (!isKindeEnabled()) return Promise.resolve(null);

	const environment = selectedEnvironment();
	const tenant = env.kinde.forEnvironment(environment);

	// 🛑 The memo is keyed on the environment, not just on existence. On staging the switch can
	// change the tenant mid-session; serving a client built for the OTHER tenant is precisely the
	// dev-token-to-prod-API mismatch this change exists to remove. Outside staging `forEnvironment`
	// is constant, so this never fires and behaviour is unchanged.
	if (clientPromise && clientEnvironment !== environment) {
		clientPromise = null;
	}

	if (!clientPromise) {
		clientEnvironment = environment;
		clientPromise = createKindeClient({
			client_id: tenant.clientId,
			domain: tenant.domain,
			audience: tenant.audience,
			redirect_uri: `${window.location.origin}/callback`,
			// 🛑 Kinde matches `logout_uri` character-for-character against the app's Allowed logout
			// redirect URLs and, on no match, silently strands the user on its own /logged-out page
			// rather than erroring. `${origin}/signin` was tried on 2026-09-17 and did exactly that.
			//
			// ⚠️ THE ORIGIN IS NOT SELF-REGISTERING. An earlier version of this note claimed it made
			// "every localhost port work without registering a URL per developer" — measured false the
			// same day: from http://localhost:5199 Kinde received that origin, did not recognise it,
			// and stranded the session. EVERY origin a developer runs on has to be registered, the
			// deployed ones included.
			logout_uri: window.location.origin,
			// openid/profile/email → the ID token + userinfo the backend reads the
			// verified email from; offline → a refresh token for silent renewal.
			scope: 'openid profile email offline',
			// Session persistence across a full refresh. First-party cookies (Kinde's
			// production pattern) are used whenever the auth domain is same-site with the
			// app — i.e. the prod custom domain auth.zezamii.com under cloud.zezamii.com,
			// which keeps the refresh token OUT of localStorage and closes the XSS-→
			// durable-takeover exposure (go-live gate E). On the dev tenant (raw
			// *.kinde.com, cross-site) that cookie is third-party-blocked, so we fall back
			// to localStorage there — dev behaviour is unchanged. See firstPartyCookiesUsable.
			// Resolved from `tenant`, not env.kinde.domain — on staging set to Production the two
			// differ, and reading the build default here would pick the storage mode for the WRONG
			// tenant (auth.zezamii.com is same-site with cloud.zezamii.com but not with the stg host).
			is_dangerously_use_local_storage: !firstPartyCookiesUsable(tenant.domain, window.location.hostname)
		}).catch((error) => {
			// Reset so a later call can retry a fresh init instead of caching the failure.
			clientPromise = null;
			clientEnvironment = null;
			// eslint-disable-next-line no-console
			console.error('Kinde client init failed', error);
			return null;
		});
	}

	return clientPromise;
};

/**
 * Redirect to Kinde's hosted login. No-op when the flag is off.
 *
 * Returns FALSE rather than throwing when there is no client — same contract as `register` below,
 * and for the same reason. `getKindeClient()` resolves to null on an init failure (it catches and
 * only console.errors, see above), so without a return value the caller cannot tell "redirecting
 * now" from "nothing will ever happen". On the sign-in page this is the difference between a
 * recoverable error and a dead button on the front door's only control.
 *
 * @returns {Promise<boolean>} true if the redirect was initiated; false when the flag is off or
 *   the client failed to init — the caller shows a fallback instead of a dead spinner.
 */
export const login = async (options) => {
	const client = await getKindeClient();
	if (!client) return false;
	await client.login(options);
	return true;
};

/** The staff Entra SSO connection for this build, or null when none is configured. */
export const getEntraConnectionId = () => env.kinde.entraConnectionId;

/**
 * Sign in straight through the staff Entra SSO connection: Kinde's `connection_id` skips its own
 * sign-in screen and redirects to Microsoft. Same return contract as `login`; also false when this
 * build has no connection configured.
 * @returns {Promise<boolean>}
 */
export const loginWithEntra = async () => {
	const connectionId = getEntraConnectionId();
	if (!connectionId) return false;
	return login({ authUrlParams: { connection_id: connectionId } });
};

/**
 * Redirect to Kinde's hosted REGISTRATION (signup). No-op when the flag is off.
 * Used by the onboarding bridge (`/welcome`): pass
 * `{ authUrlParams: { login_hint: email, prompt: 'create' } }` — `prompt=create`
 * forces the signup screen and `login_hint` prefills the invited email. The round-trip
 * returns to `/callback` (same as login), which processes the exchange and bootstraps
 * the session. Do NOT pass an `org_code` — org membership is resolved server-side by
 * verified-email match, and `org_code` is client-manipulable (G3).
 * @returns {Promise<boolean>} true if the redirect was initiated; false when the flag is
 *   off or the client failed to init — the caller shows a fallback instead of a dead spinner.
 */
export const register = async (options) => {
	const client = await getKindeClient();
	if (!client) return false;
	await client.register(options);
	return true;
};
